import React, { useState } from 'react';
import { Select, Popover, Calendar, Button, Spin } from 'antd';
import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import type { Moment } from 'moment';
import DownSvg from 'assets/svg/Down';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import { colorConstants } from 'config/colorConstants';
import { NegativeTriangle, PositiveTriangle } from 'assets/svg';
import dayjs from 'dayjs';
import Calender, { DateRangeType } from './Calender';
import { dayType } from 'utils/Calender';

const { Option } = Select;

interface UpLiftRoiDataType {
    label: string;
    value: number | string;
    mainText: string;
    roi: number;
    uplift: number;
    originalROI: number;
    originalUPlift: number;
}

interface IProps {
    data: UpLiftRoiDataType[];
    style?: React.CSSProperties;
    onChange: Function;
    value: any;
    originalROI: number;
    originalUPlift: number;
    loading: boolean;
    onClick: Function;
    onDateChange: (date: string) => void;
    startDay?: dayType;
    range?: DateRangeType;
}

const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
    .ant-select-selection-item {
        height: 30px;
        margin-left: 16px;
        font-size: 11px;
        font-weight: 500;
    }
`;

const DateSelectComboWithPopover: React.FC<IProps> = ({
    data,
    style,
    onChange,
    value,
    originalROI,
    originalUPlift,
    loading,
    onClick,
    onDateChange,
    startDay,
    range,
}) => {
    const [popoverVisible, setPopoverVisible] = useState(false);
    const currentDate = new Date();

    const handleDateSelect = (range: DateRangeType) => {
        // console.log(date);
        const formattedDate = dayjs(range[1])?.format('YYYY-MM-DD');
        onDateChange(formattedDate);

        console.log(formattedDate);
        // const epochDate = Math.floor(date.clone().utc().startOf('day').valueOf() / 1000);
        setPopoverVisible(false); // close popover on select
    };

    // const handleDateRangeOpenChange = (
    //     newOpen: boolean,
    //     isClickOutside: boolean = true,
    //     from: 'header' | 'dateRange' = 'dateRange',
    // ) => {
    //     setIsOpen(newOpen);
    //     if (!newOpen && !isClickOutside) {
    //         //only save the date when clicked on set date button
    //         const date = dayjs(new Date(tempRange[1])).format('YYYY-MM-DD');
    //         setStateForCalender((prev: any) => ({ ...prev, dateRange: tempRange }));
    //         changeInSelection('startDate', date);
    //         changeInSelection('month', dayjs(new Date(tempRange[1])).format('MMM'));
    //         trackEventMixpanel(PROMO_RECOMMENDATION_ADD_EVENT_DATE_SELECTED, {
    //             selectedDate: date,
    //         });
    //     } else if (!newOpen) setTempRange([...stateForCalender.dateRange]); //if clicked outside dont save the change
    // };

    return (
        <div style={{ position: 'relative' }}>
            {/* Select with left padding for icon */}
            <CustomSelect
                onChange={(val: any, item: any) => onChange(val, item)}
                style={{ ...style }}
                value={value}
                suffixIcon={<DownSvg />}
                className=" dropdown-width uniqSelROIUpLiftDropdown"
                onClick={() => onClick()}
            >
                {loading ? (
                    <Option>
                        <div className="flex justify-center items-center p-4">
                            <Spin />
                        </div>
                    </Option>
                ) : (
                    data.map((item, index) => {
                        const isUpliftPositive = item.uplift > originalUPlift;
                        const isRoiPositive = item.roi > originalROI;
                        return (
                            <Option item={item} value={item?.value} key={index}>
                                <div>
                                    <div className="text-fontGray font-medium font-xxs">{item.label}</div>
                                    <div className="flex gap-2">
                                        {item?.uplift && (
                                            <p className="flex gap-1 items-center">
                                                <span className="text-fontGreyAlt font-xxs">Uplift:</span>
                                                <span>
                                                    {isUpliftPositive ? <PositiveTriangle /> : <NegativeTriangle />}
                                                </span>
                                                <span
                                                    style={{
                                                        color: isUpliftPositive
                                                            ? colorConstants.limeGreen
                                                            : colorConstants.red,
                                                    }}
                                                >
                                                    {item.uplift.toFixed(2)}
                                                </span>
                                            </p>
                                        )}
                                        {item?.roi && (
                                            <p className="flex gap-1 items-center">
                                                <span className="text-fontGreyAlt font-xxs">ROI:</span>
                                                <span>
                                                    {isRoiPositive ? <PositiveTriangle /> : <NegativeTriangle />}{' '}
                                                </span>
                                                <span
                                                    style={{
                                                        color: isRoiPositive
                                                            ? colorConstants.limeGreen
                                                            : colorConstants.red,
                                                    }}
                                                >
                                                    {item.roi.toFixed(2)}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </Option>
                        );
                    })
                )}
            </CustomSelect>

            {/* Popover + Calendar icon inside Select input on the left */}
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    transform: 'translateY(-50%)',
                }}
            >
                <Popover
                    trigger="click"
                    open={popoverVisible} // updated here
                    onOpenChange={(open) => setPopoverVisible(open)}
                    placement={'right'}
                    // overlayInnerStyle={{ padding: 0 }}
                    content={
                        <Calender
                            minYear={currentDate.getFullYear()}
                            startYear={currentDate.getFullYear()}
                            maxYear={currentDate.getFullYear() + 2}
                            disabledDates={[dayjs(currentDate).format('MM-DD-YYYY'), null]}
                            startDay={startDay || 'Monday'}
                            range={range}
                            onChange={handleDateSelect}
                            // onFooterClick={() => handleOpenChange(false, false)}
                            singleDateSelect={true}
                        />
                    }
                >
                    <Button
                        type="text"
                        icon={<CalendarOutlined style={{ color: colorConstants.violet }} />}
                        style={{ padding: 0, height: 'auto' }}
                        onClick={(e) => e.stopPropagation()} // prevent Select dropdown open
                    />
                </Popover>
            </div>
        </div>
    );
};

export default DateSelectComboWithPopover;
